import React, { useEffect } from 'react'
import { Link } from 'react-router-dom'
import { motion } from 'framer-motion'
import { ChefHat, Sparkles, Heart, Users, Star } from 'lucide-react'
import { Button } from '../components/ui/Button'
import RecipeCard from '../components/recipe/RecipeCard'
import useRecipeStore from '../store/recipes'
import useAuthStore from '../store/auth'

const HomePage = () => {
  const { recipes, fetchRecipes } = useRecipeStore()
  const { isAuthenticated } = useAuthStore()

  useEffect(() => {
    fetchRecipes()
  }, [fetchRecipes])

  // Mock data for initial display
  const featuredRecipes = recipes.slice(0, 6).length > 0 ? recipes.slice(0, 6) : [
    {
      id: 1,
      title: "Magical Chocolate Chip Cookies",
      description: "Warm, gooey cookies that taste like childhood dreams",
      cookTime: 25,
      servings: 12,
      difficulty: "easy",
      category: "Dessert",
      rating: 4.9,
      image: "https://images.unsplash.com/photo-1558961363-fa8fdf82db35?w=400&h=300&fit=crop&auto=format",
      chef: { name: "Emma Baker", avatar: null }
    },
    {
      id: 2,
      title: "Rainbow Vegetable Stir Fry",
      description: "A colorful dance of fresh vegetables and flavors",
      cookTime: 15,
      servings: 4,
      difficulty: "easy",
      category: "Healthy",
      rating: 4.7,
      image: "https://images.unsplash.com/photo-1512058564366-18510be2db19?w=400&h=300&fit=crop&auto=format",
      chef: { name: "Chef Marco", avatar: null }
    },
    {
      id: 3,
      title: "Fluffy Pancake Stack",
      description: "Sky-high pancakes that melt in your mouth",
      cookTime: 20,
      servings: 6,
      difficulty: "medium",
      category: "Breakfast",
      rating: 4.8,
      image: "https://images.unsplash.com/photo-1567620905732-2d1ec7ab7445?w=400&h=300&fit=crop&auto=format",
      chef: { name: "Sophie Morning", avatar: null }
    }
  ]

  const stats = [
    { icon: ChefHat, label: "Recipes", value: "10,000+" },
    { icon: Users, label: "Chefs", value: "2,500+" },
    { icon: Heart, label: "Favorites", value: "50,000+" },
    { icon: Star, label: "Rating", value: "4.9" }
  ]

  return (
    <div className="space-y-16">
      {/* Hero Section */}
      <section className="text-center py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="max-w-4xl mx-auto"
        >
          <div className="flex justify-center mb-6">
            <motion.div
              animate={{ rotate: [0, 10, -10, 0] }}
              transition={{ duration: 2, repeat: Infinity, repeatDelay: 3 }}
              className="text-6xl"
            >
              🍳
            </motion.div>
          </div>

          <h1 className="section-title">
            Welcome to <span className="sparkle-text">Recipy</span>
          </h1>

          <p className="section-subtitle mb-8">
            Where childhood dreams meet culinary magic. Discover, create, and share recipes
            that spark joy and bring families together around the dinner table.
          </p>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" animation="sparkle" asChild>
              <Link to="/recipes">
                <Sparkles className="w-5 h-5 mr-2" />
                Explore Recipes
              </Link>
            </Button>

            {!isAuthenticated ? (
              <Button variant="outline" size="lg" asChild>
                <Link to="/register">Join the Magic</Link>
              </Button>
            ) : (
              <Button variant="warm" size="lg" asChild>
                <Link to="/create">Create Recipe</Link>
              </Button>
            )}
          </div>
        </motion.div>
      </section>

      {/* Stats Section */}
      <section className="py-8">
        <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
          {stats.map((stat, index) => {
            const Icon = stat.icon
            return (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.4, delay: index * 0.1 }}
                className="text-center"
              >
                <div className="card-dreamy p-6 hover:shadow-dreamy transition-all duration-300">
                  <div className="inline-flex items-center justify-center w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl mb-3">
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-2xl font-display font-bold text-gray-900 mb-1">
                    {stat.value}
                  </div>
                  <div className="text-sm text-gray-600">
                    {stat.label}
                  </div>
                </div>
              </motion.div>
            )
          })}
        </div>
      </section>

      {/* Featured Recipes */}
      <section>
        <div className="section-header">
          <h2 className="section-title">Featured Recipes</h2>
          <p className="section-subtitle">
            Hand-picked magical recipes from our community of dreamers
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mb-8">
          {featuredRecipes.map((recipe, index) => (
            <motion.div
              key={recipe.id}
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: index * 0.1 }}
            >
              <RecipeCard recipe={recipe} />
            </motion.div>
          ))}
        </div>

        <div className="text-center">
          <Button variant="secondary" size="lg" asChild>
            <Link to="/recipes">View All Recipes</Link>
          </Button>
        </div>
      </section>

      {/* Call to Action */}
      <section className="py-16">
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.6 }}
          className="card-dreamy text-center max-w-2xl mx-auto bg-gradient-to-br from-warm-50 to-primary-50"
        >
          <div className="p-8">
            <div className="text-4xl mb-4 floating-element">✨</div>
            <h3 className="text-3xl font-display font-bold text-dreamy mb-4">
              Ready to Create Magic?
            </h3>
            <p className="text-lg text-gray-600 mb-6">
              Join thousands of dreamers sharing their favorite recipes and creating
              beautiful food memories together.
            </p>
            <div className="flex flex-col sm:flex-row gap-3 justify-center">
              <Button size="lg" animation="float" asChild>
                <Link to="/create">Share Your Recipe</Link>
              </Button>
              <Button variant="outline" size="lg" asChild>
                <Link to="/visualizer">Try Recipe Visualizer</Link>
              </Button>
            </div>
          </div>
        </motion.div>
      </section>
    </div>
  )
}

export default HomePage